// src/pages/admin/AdminDashboard.js
// Tableau de bord administrateur connecté à l'API - NE PAS MODIFIER

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ROUTES from '../../constants/routes';
import { useAuth } from '../../hooks/useAuth';
import apiClient from '../../api/client';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const loadStats = async () => {
      setLoading(true);
      setError(false);
      try {
        const result = await apiClient.get('/endpoints/stats.php?action=dashboard');
        setStats(result);
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    loadStats();
  }, []);

  const statCards = stats
    ? [
        { label: 'Annonces publiées', value: stats.publishedListings, icon: '🏡', color: '#48BB78' },
        { label: 'En attente', value: stats.pendingListings, icon: '⏳', color: '#ED8936' },
        { label: 'Réservations', value: stats.totalReservations, icon: '📋', color: '#4299E1' },
        { label: 'Nouvelles demandes', value: stats.newReservations, icon: '🔔', color: '#F56565' },
      ]
    : [];

  const menuCards = [
    { label: 'Annonces', icon: '🏡', path: ROUTES.ADMIN_LISTINGS, desc: 'Gérer les annonces' },
    { label: 'Réservations', icon: '📋', path: ROUTES.ADMIN_RESERVATIONS, desc: 'Demandes clients' },
    { label: 'Propriétaires', icon: '👥', path: ROUTES.ADMIN_OWNERS, desc: 'Gérer les propriétaires' },
    { label: 'Quartiers', icon: '📍', path: ROUTES.ADMIN_QUARTIERS, desc: 'Gérer les quartiers' },
    { label: 'Types', icon: '🏷️', path: ROUTES.ADMIN_TYPES, desc: "Types d'annonces" },
    { label: 'Utilisateurs', icon: '👤', path: ROUTES.ADMIN_USERS, desc: 'Gérer les utilisateurs' },
    { label: 'Administrateurs', icon: '🔑', path: ROUTES.ADMIN_ADMINS, desc: 'Gérer les admins' },
    { label: 'Statistiques', icon: '📊', path: ROUTES.ADMIN_STATS, desc: 'Voir les stats' },
    { label: "Logs d'audit", icon: '📜', path: ROUTES.ADMIN_AUDIT_LOGS, desc: 'Historique des actions' },
  ];

  const handleLogout = async () => {
    await logout();
    navigate(ROUTES.ADMIN_LOGIN);
  };

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      maxWidth: '900px',
      margin: '0 auto',
      backgroundColor: '#0A0E1A',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '16px 20px',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      position: 'sticky',
      top: 0,
      backgroundColor: '#0A0E1A',
      zIndex: 50,
    },
    logo: { fontSize: '20px', fontWeight: '700', color: '#FFD700' },
    logoutButton: {
      padding: '8px 16px',
      backgroundColor: 'rgba(245, 101, 101, 0.1)',
      border: '1px solid rgba(245, 101, 101, 0.3)',
      borderRadius: '8px',
      color: '#F56565',
      fontSize: '13px',
      fontWeight: '600',
      cursor: 'pointer',
    },
    title: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#FFFFFF',
      padding: '20px',
    },
    stateMessage: {
      padding: '0 20px 20px',
      fontSize: '13px',
      color: '#A0AEC0',
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '12px',
      padding: '0 20px 20px',
    },
    statCard: {
      padding: '16px',
      backgroundColor: '#162033',
      borderRadius: '12px',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    statIcon: { fontSize: '24px' },
    statValue: { fontSize: '28px', fontWeight: '700', color: '#FFFFFF' },
    statLabel: { fontSize: '13px', color: '#A0AEC0' },
    sectionTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#FFFFFF',
      padding: '8px 20px 12px',
    },
    menuGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
      gap: '12px',
      padding: '0 20px 40px',
    },
    menuCard: {
      padding: '20px 16px',
      backgroundColor: '#162033',
      borderRadius: '12px',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      transition: 'all 200ms',
    },
    menuIcon: { fontSize: '28px' },
    menuLabel: { fontSize: '15px', fontWeight: '600', color: '#FFFFFF' },
    menuDesc: { fontSize: '12px', color: '#A0AEC0' },
  };

  return (
    <div style={styles.container}>
      <Helmet>
        <title>Dashboard Admin | Fynho</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div style={styles.header}>
        <div style={styles.logo}>Fynho Admin</div>
        <button style={styles.logoutButton} onClick={handleLogout}>
          Déconnexion
        </button>
      </div>

      <div style={styles.title}>Tableau de bord</div>

      {loading && <div style={styles.stateMessage}>Chargement des statistiques...</div>}
      {!loading && error && (
        <div style={styles.stateMessage}>Impossible de charger les statistiques pour le moment.</div>
      )}

      {!loading && !error && stats && (
        <div style={styles.statsGrid}>
          {statCards.map((stat, index) => (
            <div key={index} style={styles.statCard}>
              <span style={styles.statIcon}>{stat.icon}</span>
              <span style={{ ...styles.statValue, color: stat.color }}>{stat.value}</span>
              <span style={styles.statLabel}>{stat.label}</span>
            </div>
          ))}
        </div>
      )}

      <div style={styles.sectionTitle}>Gestion</div>

      <div style={styles.menuGrid}>
        {menuCards.map((card, index) => (
          <div
            key={index}
            style={styles.menuCard}
            onClick={() => navigate(card.path)}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#1C2537';
              e.currentTarget.style.borderColor = 'rgba(255, 215, 0, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#162033';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
            }}
          >
            <span style={styles.menuIcon}>{card.icon}</span>
            <span style={styles.menuLabel}>{card.label}</span>
            <span style={styles.menuDesc}>{card.desc}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminDashboard;